import React, { useState, useEffect } from "react";
import "../Styles/Tutorial.css";
import "../Styles/Home.css";
import { Link, useParams } from "react-router-dom";
import TutorialSidebar from "./TutorialSidebar";
import config from "../../config/Config";
import { Helmet } from "react-helmet";
import Loader from "./Loader";
import NotFound from "./NotFound";
import ListingHero from "./ListingHero";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { CardImageThumb } from "./CardImageThumb";

export default function TutorialSeries() {
  const [configuration, setConfiguration] = useState([]);
  const [datas, setDatas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [imageLightboxSrc, setImageLightboxSrc] = useState(null);
  const { id } = useParams();

  async function fetchSeries() {
    try {
      const url = id
        ? `${config.API_TUTORIAL}getseries?key=${id}`
        : `${config.API_TUTORIAL}getseries`;

      const result = await fetch(url);
      const res = await result.json();
      setDatas(res);
    } catch (err) {
      console.error("Error fetching series:", err);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchConfiguration() {
    try {
      const result = await fetch(`${config.API_BLOG}configuration`);
      const res = await result.json();
      setConfiguration(res);
    } catch (err) {
      console.error("Error fetching configuration:", err);
    }
  }

  const toSlug = (title) =>
    title
      .toLowerCase()
      .replace(/ /g, "-")
      .replace(/[^\w-]+/g, "");

  useEffect(() => {
    setIsLoading(true);
    fetchSeries();
    fetchConfiguration();
  }, [id]);

  const metaTitle = configuration[0]?.default_meta_title;
  const metaKeywords = configuration[0]?.default_meta_keywords;
  const metaDescription = configuration[0]?.default_meta_description;

  const siteLabel =
    configuration[0]?.default_meta_title?.split("|")[0]?.trim() || "iLearnings";

  const renderHTML = (rawHTML) => ({ __html: rawHTML });

  const filteredData = datas.filter((item) => item.status === 1);

  return (
    <div className="container-fluid page-shell mt-4 home-page">
      {isLoading ? (
        <div className="text-center mt-5 py-5">
          <Loader />
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-5">
          <NotFound />
        </div>
      ) : (
        <>
          <Helmet>
            <title>TutorialSeries</title>
            <meta name="title" content={metaTitle} />
            <meta name="keywords" content={metaKeywords} />
            <meta name="description" content={metaDescription} />
          </Helmet>

          <ListingHero
            siteLabel={siteLabel}
            description="Follow structured tutorial series — step-by-step lessons, technologies, and hands-on practice to level up your skills."
            primaryStat={filteredData.length}
            primaryStatLabel="Tutorial series"
            primaryCtaTo="/"
            primaryCtaLabel=""
            secondaryCtaTo="/blog"
            secondaryCtaLabel=""
          />

          <section
            className="home-section"
            aria-labelledby="tutorial-list-heading"
          >
            <div className="home-section__head" data-aos="fade-up">
              <center style={{ fontSize: "38px", fontFamily: "sofia" }}>
                <b>
                  Tutorial <b style={{ color: "#173a9b" }}>series </b>
                </b>
                <hr
                  className="w-25 mx-auto"
                  style={{ transform: "translateY(-16px)" }}
                />
              </center>
            </div>

            <div className="row g-4 home-card-grid justify-content-center">
              {filteredData.map((item, index) => {
                const limitedContentTutorials = (() => {
                  const parser = new DOMParser();
                  const plainText =
                    parser.parseFromString(
                      item.tutorial_series_description,
                      "text/html",
                    ).body.textContent || "";
                  const letterOnlyText = plainText.replace(/[^a-zA-Z ]/g, "");
                  let letterCount = 0;
                  let limitedText = "";
                  for (let i = 0; i < letterOnlyText.length; i++) {
                    if (/[a-zA-Z]/.test(letterOnlyText[i])) {
                      letterCount++;
                    }
                    if (letterCount <= 200) {
                      limitedText += letterOnlyText[i];
                    } else {
                      break;
                    }
                  }
                  return limitedText + (letterCount > 200 ? "..." : "");
                })();

                return (
                  <div
                    className="col-12 col-xl-6 card_box"
                    key={item.id}
                    data-aos="fade-up"
                    style={{ transform: "translateY(-25px)" }}
                    data-aos-delay={Math.min(index * 70, 350)}
                  >
                    <article
                      className="card home-card h-100"
                      style={{ border: "1px solid rgba(23, 58, 155, 0.18)" }}
                    >
                      <div className="home-card__body">
                        <div className="home-card__thumb">
                          <CardImageThumb
                            src={item.tutorial_series_image_b64}
                            alt={item.tutorial_series_name}
                            onOpen={setImageLightboxSrc}
                          />
                        </div>
                        <div className="home-card__content">
                          <Link
                            to={`/tutorial/${toSlug(item.tutorial_series_name)}`}
                            state={{ item }}
                            className="home-card__title"
                          >
                            {item.tutorial_series_name}
                          </Link>
                          <div
                            className="home-card__excerpt para_font_family"
                            dangerouslySetInnerHTML={renderHTML(
                              limitedContentTutorials,
                            )}
                          />
                          <Link
                            to={`/tutorial/${toSlug(item.tutorial_series_name)}`}
                            state={{ item }}
                            className="home-card__read"
                          >
                            Open series →
                          </Link>
                        </div>
                      </div>
                      <div className="home-card__tags">
                        <div className="home-card__footer">
                          <div className="home-card__footer-inner para_font_family text-light">
                            <p style={{ textTransform: "capitalize" }}>
                              <b>Lessons</b> {item.lessons_count}
                            </p>
                            <p>
                              <b>Tech</b> {item.technologies}
                            </p>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="side1">
            <TutorialSidebar />
          </div>

          {imageLightboxSrc ? (
            <Lightbox
              mainSrc={imageLightboxSrc}
              onCloseRequest={() => setImageLightboxSrc(null)}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
